import React, { Fragment, useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Row, Col, Card, Badge } from "react-bootstrap";
import {
  FiUser,
  FiCalendar,
  FiEdit,
  FiInfo,
  FiMapPin,
  FiLink2,
  FiEye,
  FiBarChart2,
} from "react-icons/fi";
import {
  getFormResponseList,
  getFormResponseDetails,
  deleteFormResponse,
  updateFormResponseStatus,
} from "../../../services/Users-services";
import {openModal} from "../../../services/redux/reducers/modalSlice";
import {openNewForm} from "../../../services/redux/reducers/customFormBuildeSclice";
// Import necessary components and services
import {
  getFormsDetails,
  updateFormStatus,
  deleteForms,
} from "../../../services/Users-services";
import { showErrorMessages } from "../../../services/utilityFunctions.js";
import DetailsPageNavigation from "../../../views/dashboard/widget/detailsPageNavigation";
import MasterActions from "../../../views/dashboard/widget/MasterActions";
import { getMethods } from "../../../services/utilityFunctions";
import MasterTable from "../../../views/dashboard/widget/MasterTabel";
import { useSelector, useDispatch } from "react-redux";
import FormPreview from "../formPreview";
const FormDetailsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [methods, setMethods] = useState({});
  const isInitialMount = useRef(true);
  const dispatch = useDispatch();
  // Fetch form details
  const fetchDetails = async ({ id, isLoading = true }) => {
    if (isLoading) setLoading(true);
    setError(null); // Reset error state before fetch

    try {
      if (!id) {
        console.log("Form ID is not provided");
        throw new Error("Form ID is required");
      }

      const response = await getFormsDetails(id);
      console.log("Form details response:", response);
      if (response.status === "success") {
        console.log("Form details:", response.data);
        setDetails(response.data);
        // Get methods for the actions dropdown
        setMethods(getMethods("Forms", "Forms"));
      } else {
        showErrorMessages({ data: response, navigate });
        setError("Failed to load form details. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching form details:", error);
      setError(
        error.message || "An error occurred while fetching form details"
      );
    } finally {
      if (isLoading) setLoading(false);
    }
  };

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
    fetchDetails({ id });
  }, [id]);

  // Parse the form metadata
  // Enhanced getFormMetadata function with better error handling
  const getFormMetadata = () => {
    if (!details || !details["_METADATA_"]) {
      console.log("Missing metadata in form details");
      return [];
    }

    try {
      // Add some logging to debug the metadata
      console.log("Raw metadata:", details["_METADATA_"]);

      const parsed = JSON.parse(details["_METADATA_"]);
      console.log("Parsed metadata:", parsed);

      // Validate that the parsed data is in the expected format
      if (!Array.isArray(parsed)) {
        console.warn("Metadata is not an array, converting to array format");
        return [parsed]; // Convert to array with single element
      }

      return parsed;
    } catch (e) {
      console.error("Error parsing form metadata:", e);
      return [];
    }
  };

  // Enhanced handlePreviewForm function with better checks and error messages
  // Function to handle preview form
  const handlePreviewForm = () => {
    const formMetadata = getFormMetadata();
    console.log("Form Metadata:", formMetadata);

    if (
      formMetadata !== null &&
      (Array.isArray(formMetadata) ? formMetadata.length > 0 : true)
    ) {
      dispatch(openNewForm({ metadata: formMetadata }));
      dispatch(
        openModal({
          isOpen: true,
          title: "Form Preview",
          size: "lg",
          isEditing: false,
          isHeader: false,
          body: (
            <FormPreview
              isModal={true}
              navigate={navigate}
              onClose={() => dispatch(openModal({ isOpen: false }))}
            />
          ),
        })
      );
    } else {
      console.error("Invalid or empty form metadata:", formMetadata);
      alert(
        "Form is not available for preview. The form metadata is empty or invalid."
      );
    }
  };

  // Function to copy form link
  const handleCopyLink = () => {
    const formLink = `${window.location.origin}/form/${id}`;
    navigator.clipboard.writeText(formLink);
    window.open(formLink, "_blank");
    // You can add toast notification here
    // alert("Form link copied to clipboard!");
  };

  // Check if the data is properly loaded and has necessary properties
  const isDataComplete =
    details &&
    typeof details === "object" &&
    "NAME" in details &&
    "STATUS" in details;

  // Render loading, error, or content accordingly
  if (loading) {
    return (
      <center>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </center>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger m-3" role="alert">
        <h4 className="alert-heading">Error!</h4>
        <p>{error}</p>
        <hr />
        <p className="mb-0">
          <button
            className="btn btn-outline-danger"
            onClick={() => fetchDetails({ id })}
          >
            Try Again
          </button>
          <button
            className="btn btn-outline-secondary ms-2"
            onClick={() => navigate(-1)}
          >
            Go Back
          </button>
        </p>
      </div>
    );
  }

  if (!isDataComplete) {
    return (
      <div className="alert alert-warning m-3" role="alert">
        <h4 className="alert-heading">Incomplete Data</h4>
        <p>The form details are incomplete or improperly formatted.</p>
        <hr />
        <p className="mb-0">
          <button
            className="btn btn-outline-warning"
            onClick={() => fetchDetails({ id })}
          >
            Refresh
          </button>
          <button
            className="btn btn-outline-secondary ms-2"
            onClick={() => navigate(-1)}
          >
            Go Back
          </button>
        </p>
      </div>
    );
  }

  // Get number of responses (you might want to replace this with actual data)
  const responseCount = details.responseCount || 0;

  return (
    <Fragment>
      <Row className="mb-3">
        <Row className="mb-1 pb-0">
          <Col lg="12" className="mb-0 pb-0">
            <Card
              className="position-relative border-0 transition-all shadow-sm"
              style={{ borderRadius: "1rem", transition: "0.3s" }}
            >
              <Card.Body className="p-3 px-lg-4 py-lg-3 pe-1">
                <Row className="">
                  {/* Left Section - Form Info */}
                  <Col lg="9">
                    <div className="">
                      <DetailsPageNavigation list={["Forms", "Details"]} />
                      <div className="d-flex align-items-start gap-2 mb-1">
                        <h3
                          className="mb-0 fw-bold fs-4 text-truncate"
                          style={{
                            background:
                              "linear-gradient(45deg, #2b2b2b, #4a4a4a)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                          }}
                        >
                          {details?.NAME || "Untitled Form"}
                        </h3>
                        <Badge
                          bg=""
                          className="mt-0"
                          style={{
                            padding: "4px 7px",
                            fontSize: "0.65rem",
                            fontWeight: "400",
                            backgroundColor:
                              details?.["STATUS"] === "Y"
                                ? "#e8f5e9"
                                : "#ffebee",
                            color:
                              details?.["STATUS"] === "Y"
                                ? "#2e7d32"
                                : "#c62828",
                            borderRadius: "4px",
                            textTransform: "uppercase",
                            letterSpacing: "0.4px",
                          }}
                        >
                          {details?.["STATUS"] === "Y"
                            ? "Published"
                            : "Unpublished"}
                        </Badge>
                      </div>
                      <p
                        className="text-muted mb-5"
                        style={{
                          fontSize: "0.875rem",
                          lineHeight: 1.5,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          opacity: 0.75,
                        }}
                      >
                        {details?.DESCRIPTION || "No description available"}
                      </p>

                      {/* Key Details Section - Improved Layout */}
                      <div className="d-flex flex-wrap gap-4 mb-2">
                        {/* Link Icon */}
                        <div className="d-flex align-items-center hover-animation">
                          <div
                            className="d-flex align-items-center justify-content-center rounded-circle me-2"
                            style={{
                              width: "24px",
                              height: "24px",
                              backgroundColor: "rgba(33, 150, 243, 0.15)",
                            }}
                          >
                            <FiLink2
                              style={{ color: "#1976D2", fontSize: "16px" }}
                              onClick={handleCopyLink}
                            />
                          </div>
                          <div>
                            <div
                              className="text-muted"
                              style={{
                                fontSize: "0.7rem",
                                fontWeight: "600",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Form Link
                            </div>
                            <div
                              className="fw-medium"
                              style={{ fontSize: "0.8rem", cursor: "pointer" }}
                              onClick={handleCopyLink}
                            >
                              Copy Link
                            </div>
                          </div>
                        </div>

                        {/* Preview Form */}
                        <div className="d-flex align-items-center hover-animation">
                          <div
                            className="d-flex align-items-center justify-content-center rounded-circle me-2"
                            style={{
                              width: "24px",
                              height: "24px",
                              backgroundColor: "rgba(233, 30, 99, 0.15)",
                            }}
                          >
                            <FiEye
                              style={{ color: "#C2185B", fontSize: "16px" }}
                              onClick={handlePreviewForm}
                            />
                          </div>
                          <div>
                            <div
                              className="text-muted"
                              style={{
                                fontSize: "0.7rem",
                                fontWeight: "600",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Form Preview
                            </div>
                            <div
                              className="fw-medium"
                              style={{ fontSize: "0.8rem", cursor: "pointer" }}
                              onClick={handlePreviewForm}
                            >
                              View Preview
                            </div>
                          </div>
                        </div>

                        {/* Response Count */}
                        {/* <div className="d-flex align-items-center hover-animation">
                          <div
                            className="d-flex align-items-center justify-content-center rounded-circle me-2"
                            style={{
                              width: "24px",
                              height: "24px",
                              backgroundColor: "rgba(76, 175, 80, 0.15)",
                            }}
                          >
                            <FiBarChart2
                              style={{ color: "#388E3C", fontSize: "16px" }}
                            />
                          </div>
                          <div>
                            <div
                              className="text-muted"
                              style={{
                                fontSize: "0.7rem",
                                fontWeight: "600",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Responses
                            </div>
                            <div
                              className="fw-medium"
                              style={{ fontSize: "0.8rem" }}
                            >
                              {responseCount} total
                            </div>
                          </div>
                        </div> */}

                        {/* Form Type (optional, if available in your data) */}
                        {/* <div className="d-flex align-items-center hover-animation">
                          <div
                            className="d-flex align-items-center justify-content-center rounded-circle me-2"
                            style={{
                              width: "24px",
                              height: "24px",
                              backgroundColor: "rgba(255, 193, 7, 0.15)",
                            }}
                          >
                            <FiInfo
                              style={{ color: "#FFA000", fontSize: "16px" }}
                            />
                          </div>
                          <div>
                            <div
                              className="text-muted"
                              style={{
                                fontSize: "0.7rem",
                                fontWeight: "600",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Form Type
                            </div>
                            <div
                              className="fw-medium"
                              style={{ fontSize: "0.8rem" }}
                            >
                              {details?.FORM_TYPE || "Standard"}
                            </div>
                          </div>
                        </div> */}
                      </div>
                    </div>
                  </Col>

                  {/* Right Section - Metadata */}
                  <Col lg="3" className="">
                    <div className="d-flex flex-column gap-1 justify-content-between h-100">
                      {/* Created By Info */}
                      <div className="d-flex align-items-center gap-1 p-1 hover-bg-light rounded-2 small">
                        <div
                          className="bg-light rounded-circle p-1 me-2 d-flex align-items-center justify-content-center"
                          style={{ width: 28, height: 28 }}
                        >
                          <FiUser className="text-primary fs-6" />
                        </div>
                        <div>
                          <div
                            className="text-dark fw-medium"
                            style={{ fontSize: "0.875rem" }}
                          >
                            {details?.["CREATED BY"] || "Unknown"}
                          </div>
                        </div>
                      </div>

                      {/* Last Modified Date */}
                      <div className="d-flex align-items-center gap-1 p-1 hover-bg-light rounded-2 small">
                        <div
                          className="bg-light rounded-circle p-1 me-2 d-flex align-items-center justify-content-center"
                          style={{ width: 28, height: 28 }}
                        >
                          <FiCalendar className="text-success fs-6" />
                        </div>
                        <div>
                          <div
                            className="text-dark fw-medium"
                            style={{ fontSize: "0.875rem" }}
                          >
                            {details?.["LAST MODIFIED"] || "N/A"}
                          </div>
                        </div>
                      </div>

                      {/* Modified By */}
                      <div className="d-flex align-items-center gap-1 p-1 hover-bg-light rounded-2 small">
                        <div
                          className="bg-light rounded-circle p-1 me-2 d-flex align-items-center justify-content-center"
                          style={{ width: 28, height: 28 }}
                        >
                          <FiEdit className="text-warning fs-6" />
                        </div>
                        <div>
                          <div
                            className="text-dark fw-medium"
                            style={{ fontSize: "0.875rem" }}
                          >
                            {details?.["MODIFIED BY"] || "Unknown"}
                          </div>
                        </div>
                      </div>

                      {/* Actions Button */}
                      <div className="d-flex align-items-center gap-1 p-1 hover-bg-light rounded-2 small">
                        <div
                          className="bg-light rounded-circle p-1 me-2 d-flex align-items-center justify-content-center"
                          style={{ width: 28, height: 28 }}
                        >
                          <MasterActions
                            actions={{
                              edit: details["STATUS"] === "N",
                              delete: true,
                              status: true,
                            }}
                            title="Form"
                            
                            refresh={() => {
                              fetchDetails({ id, isLoading: false });
                            }}
                            methods={methods}
                            deleteRow={deleteForms}
                            updateStatus={updateFormStatus}
                            updateRow={(id, data) =>
                              console.log("Update form", id, data)
                            }
                            isDetailsPage={true}
                            item={details}
                          />
                        </div>
                        <div>
                          <div
                            className="text-dark fw-medium"
                            style={{ fontSize: "0.875rem" }}
                          >
                            {"Show Options"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Form Responses Section */}
        <Row className="mt-0">
          {details.STATUS === "Y" ? (
            <Col lg="12">
              <MasterTable
               isAdd={false}
                formId={id}
                getTableData={getFormResponseList}
                updateStatus={updateFormResponseStatus}
                deleteRow={deleteFormResponse}
                tab1={"Forms"}
                tab2={"Forms Response"}
                actions={{
                  status: true,
                  edit: true,
                  delete: true,
                }}
              />
            </Col>
          ) : (
            <Col lg="12">
              <Card
                className="position-relative border-0 transition-all"
                style={{ borderRadius: "1rem", transition: "0.3s" }}
              >
                <Card.Body className="p-3 px-lg-4 py-lg-3">
                  <div className="alert alert-info">
                    Please activate this form to view responses.
                  </div>
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>
      </Row>
    </Fragment>
  );
};

export default FormDetailsPage;
